import React from 'react'
import { FeeAmount } from '@uniswap/v3-sdk'
import { AutoColumn } from 'components/Column'
import { DynamicSection } from 'pages/AddLiquidity/styled'
import { TYPE } from 'theme'
import { AutoRow } from 'components/Row'
import { ButtonOutlined, ButtonRadioChecked } from 'components/Button'
import styled from 'styled-components'
import TagButton from 'components/TagButton'
import { Currency, Token } from '@uniswap/sdk-core'
import { Field } from 'state/mint/actions'
import axios from 'axios'
import { API_POOL_BASE_URL } from 'constants/links'
import { CURRENT_CHAIN, WETH9_ADDRESSES } from '@hyperswap/addresses'
import { roundToDecimalPlace } from 'utils/data'
import Tag from 'components/Tag'

const ResponsiveText = styled(TYPE.label)`
  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 12px;
  `};
`
const GridContainer = styled.div`
  margin: auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* 2 items per row */
  gap: 12px;
  width: 100%;
`
const FadeContainer = styled.div`
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transition: opacity 0.3s ease-in-out, max-height 0.3s ease-in-out;

  &.open {
    opacity: 1;
    max-height: 500px;
  }
`

export default function FeeSelector({
  disabled = false,
  feeAmount,
  handleFeePoolSelect,
  currencies,
}: {
  disabled?: boolean
  feeAmount?: FeeAmount
  handleFeePoolSelect: (feeAmount: FeeAmount) => void
  currencies?: { [field in Field]?: Currency }
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [tvl, setTvl] = React.useState<Record<number, number> | null>(null)
  const [highestTvlFeeAmount, setHighestTvlFeeAmount] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (currencies?.CURRENCY_A && currencies?.CURRENCY_B) {
      setLoading(true)

      const token0Address =
        currencies.CURRENCY_A instanceof Token ? currencies.CURRENCY_A.address : WETH9_ADDRESSES[CURRENT_CHAIN]
      const tokenBAddress =
        currencies.CURRENCY_B instanceof Token ? currencies.CURRENCY_B.address : WETH9_ADDRESSES[CURRENT_CHAIN]

      axios
        .get(`${API_POOL_BASE_URL}/api/pair?token0=${token0Address}&token1=${tokenBAddress}`)
        .then((response: any) => {
          const tvlList: Record<number, number> = {}
          let highestTVLFee = 0

          response.data.data.pairs?.forEach((pair: any) => {
            if (pair.version === 'v3' && pair.h24 && pair.h24.length > 0) {
              const tvlUsd = parseFloat(pair.h24)
              tvlList[pair.fee] = tvlUsd
              if (tvlUsd > (tvlList[highestTVLFee] ?? 0)) {
                highestTVLFee = pair.fee
              }
            }
          })

          setHighestTvlFeeAmount(highestTVLFee > 0 ? highestTVLFee : null)
          setTvl(tvlList)
          setLoading(false)
          if (highestTVLFee && highestTVLFee !== 0 && feeAmount === undefined) {
            handleFeePoolSelect(highestTVLFee)
            setIsOpen(false)
          }
        })
        .catch((e: any) => {
          console.error(e)
          setLoading(false)
        })
    }
  }, [currencies?.CURRENCY_A, currencies?.CURRENCY_B, handleFeePoolSelect, feeAmount])

  const DESCRIPTIONS = {
    [FeeAmount.LOWER]: 'Best for very stable pairs.',
    [FeeAmount.LOW]: 'Best for stable pairs.',
    [FeeAmount.MEDIUM]: 'Best for most pairs.',
    [FeeAmount.HIGH]: 'Best for exotic pairs.',
  }

  return (
    <AutoColumn gap="16px">
      <DynamicSection gap="md" disabled={disabled || loading}>
        <TYPE.label>Select a Pool</TYPE.label>
        <TYPE.main fontSize={14} fontWeight={400} style={{ marginBottom: '.5rem', lineHeight: '125%' }}>
          Select a pool type based on your preferred liquidity provider fee.
        </TYPE.main>

        <AutoRow>
          <ButtonOutlined
            onClick={() => setIsOpen(!isOpen)}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <ResponsiveText style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {feeAmount && feeAmount / 10000}% fee{' '}
                {highestTvlFeeAmount === feeAmount && <Tag variant={1}>Highest 1D Volume</Tag>}
              </ResponsiveText>
              <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                {feeAmount ? DESCRIPTIONS[feeAmount] : ''}
              </TYPE.main>
            </div>

            <TagButton onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-up"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M6 15l6 -6l6 6" />
                  </svg>
                  Hide
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M6 9l6 6l6 -6" />
                  </svg>
                  More
                </>
              )}
            </TagButton>
          </ButtonOutlined>
        </AutoRow>
        {isOpen && (
          <FadeContainer className={isOpen ? 'open' : ''}>
            <GridContainer>
              <ButtonRadioChecked
                width="100%"
                active={feeAmount === FeeAmount.LOWER}
                onClick={() => handleFeePoolSelect(FeeAmount.LOWER)}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText>0.01% fee</ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    Best for very stable pairs.
                  </TYPE.main>
                  {tvl?.[100] && (
                    <TYPE.main fontWeight={400} fontSize="10px" textAlign="left">
                      ${roundToDecimalPlace(`${tvl?.[100]}`, '2')} 1D Volume
                    </TYPE.main>
                  )}
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked
                width="100%"
                active={feeAmount === FeeAmount.LOW}
                onClick={() => handleFeePoolSelect(FeeAmount.LOW)}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText>0.05% fee</ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    Best for stable pairs.
                  </TYPE.main>
                  {tvl?.[500] && (
                    <TYPE.main fontWeight={400} fontSize="10px" textAlign="left">
                      ${roundToDecimalPlace(`${tvl?.[500]}`, '2')} 1D Volume
                    </TYPE.main>
                  )}
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked
                width="100%"
                active={feeAmount === FeeAmount.MEDIUM}
                onClick={() => handleFeePoolSelect(FeeAmount.MEDIUM)}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText>0.3% fee</ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    Best for most pairs.
                  </TYPE.main>
                  {tvl?.[3000] && (
                    <TYPE.main fontWeight={400} fontSize="10px" textAlign="left">
                      ${roundToDecimalPlace(`${tvl?.[3000]}`, '2')} 1D Volume
                    </TYPE.main>
                  )}
                </AutoColumn>
              </ButtonRadioChecked>
              <ButtonRadioChecked
                width="100%"
                active={feeAmount === FeeAmount.HIGH}
                onClick={() => handleFeePoolSelect(FeeAmount.HIGH)}
              >
                <AutoColumn gap="sm" justify="flex-start">
                  <ResponsiveText>1% fee</ResponsiveText>
                  <TYPE.main fontWeight={400} fontSize="12px" textAlign="left">
                    Best for exotic pairs.
                  </TYPE.main>
                  {tvl?.[10000] && (
                    <TYPE.main fontWeight={400} fontSize="10px" textAlign="left">
                      ${roundToDecimalPlace(`${tvl?.[10000]}`, '2')} 1D Volume
                    </TYPE.main>
                  )}
                </AutoColumn>
              </ButtonRadioChecked>
            </GridContainer>
          </FadeContainer>
        )}
      </DynamicSection>
    </AutoColumn>
  )
}
